<template>
  <div>
    <v-card class="elevation-2">
      <v-toolbar flat>
        <v-btn icon class="mr-1" @click="goBack">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <v-toolbar-title class="subtitle-1">
          {{ $t('sharedAccounts.stats.heading') }}
        </v-toolbar-title>
        <v-spacer />
        <v-btn icon :disabled="!arenaId || loading" @click="fetchStats">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </v-toolbar>
      <v-divider />

      <!-- Date range -->
      <v-row class="px-4 pt-4 mb-1" align="center" justify="start">
        <v-col cols="12" sm="6" md="3">
          <v-select
            v-model="preset"
            :items="presetOptions"
            :disabled="!arenaId || loading"
            :label="$t('sharedAccounts.stats.range.label')"
            solo
            hide-details="auto"
            @change="onPresetChange" />
        </v-col>
        <template v-if="preset === 'custom'">
          <v-col cols="12" sm="6" md="3">
            <v-menu
              v-model="fromMenu"
              :close-on-content-click="false"
              offset-y
              min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  :value="fromDate"
                  :label="$t('sharedAccounts.stats.from')"
                  prepend-inner-icon="mdi-calendar"
                  readonly
                  solo
                  hide-details="auto"
                  v-bind="attrs"
                  v-on="on" />
              </template>
              <v-date-picker
                v-model="fromDate"
                :locale="$i18n.locale"
                :max="toDate || todayDate"
                @input="fromMenu = false" />
            </v-menu>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-menu
              v-model="toMenu"
              :close-on-content-click="false"
              offset-y
              min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  :value="toDate"
                  :label="$t('sharedAccounts.stats.to')"
                  prepend-inner-icon="mdi-calendar"
                  readonly
                  solo
                  hide-details="auto"
                  v-bind="attrs"
                  v-on="on" />
              </template>
              <v-date-picker
                v-model="toDate"
                :locale="$i18n.locale"
                :min="fromDate"
                :max="todayDate"
                @input="toMenu = false" />
            </v-menu>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-btn
              color="primary"
              :disabled="!arenaId || loading || !fromDate || !toDate"
              @click="fetchStats">
              {{ $t('sharedAccounts.stats.apply') }}
            </v-btn>
          </v-col>
        </template>
      </v-row>

      <div v-if="resolvedWindow" class="px-4 pb-2 caption text--secondary">
        {{ resolvedWindow }}
      </div>

      <v-divider />

      <div class="pa-4">
        <div v-if="!arenaId" class="text-center py-8 mb-0">
          {{ $t('sharedAccounts.chooseArena') }}
        </div>

        <div v-else-if="loading" class="text-center py-8">
          <v-progress-circular indeterminate color="primary" />
          <p class="mt-3 mb-0">{{ $t('components.noData.fetchingEntries') }}</p>
        </div>

        <v-alert v-else-if="error" type="error" text>
          {{ error }}
        </v-alert>

        <template v-else-if="stats">
          <v-alert v-if="showTrackingNote" type="info" text dense class="mb-4">
            {{ trackingNote }}
          </v-alert>

          <!-- Summary cards -->
          <v-row dense>
            <v-col
              v-for="card in summaryCards"
              :key="card.key"
              cols="6"
              sm="4"
              md="3">
              <v-card outlined class="pa-3 fill-height">
                <div
                  class="caption text--secondary d-flex align-center"
                  style="min-width: 0">
                  <span class="text-truncate">{{ card.label }}</span>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        x-small
                        class="ml-1 flex-shrink-0 text--disabled"
                        style="cursor: help"
                        v-bind="attrs"
                        v-on="on">
                        mdi-help-circle-outline
                      </v-icon>
                    </template>
                    <div style="max-width: 260px">{{ card.tip }}</div>
                  </v-tooltip>
                </div>
                <div
                  class="text-h5 font-weight-medium"
                  :class="card.color ? card.color + '--text' : ''">
                  {{ card.value }}
                </div>
              </v-card>
            </v-col>
          </v-row>

          <!-- By game -->
          <div class="d-flex align-center mt-6 mb-2">
            <h3 class="subtitle-1 mb-0">
              {{ $t('sharedAccounts.stats.byGame') }}
            </h3>
          </div>

          <v-data-table
            v-if="byGame.length"
            :headers="headers"
            :items="byGame"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            :items-per-page="25"
            :footer-props="{ 'items-per-page-options': [10, 25, 50, 100] }"
            class="elevation-1">
            <template v-slot:[`item.game`]="{ item }">
              <div class="d-flex align-center py-1">
                <v-avatar
                  v-if="item.app && item.app.icon"
                  size="20"
                  class="mr-2"
                  tile>
                  <v-img :src="item.app.icon" contain />
                </v-avatar>
                <v-icon v-else small class="mr-2 text--secondary">
                  mdi-google-controller
                </v-icon>
                <div style="min-width: 0">
                  <div class="text-truncate">{{ item.game.name }}</div>
                  <div class="caption text--secondary text-truncate">
                    {{ item.app && item.app.name }}
                  </div>
                </div>
              </div>
            </template>
            <template v-slot:[`item.fail_rate`]="{ item }">
              <span :class="failRateColor(item.fail_rate) + '--text'">
                {{ formatPercent(item.fail_rate) }}
              </span>
            </template>
          </v-data-table>

          <p v-else class="text-center text--secondary py-8 mb-0">
            {{ $t('sharedAccounts.stats.noActivity') }}
          </p>
        </template>
      </div>
    </v-card>
  </div>
</template>

<script>
import SharedAccountsApi from '@/services/configuration/SharedAccountsApi'
import { mapActions } from 'vuex'

const DAY_MS = 24 * 60 * 60 * 1000

// Failed-attempt tracking began at this deploy (borrows were already tracked).
// We warn when the selected window reaches before it, since failures and the
// fail rate are incomplete for earlier dates.
const STATS_TRACKING_START = '2026-06-16'

// Build a local 'YYYY-MM-DD' string from a Date (for the date pickers).
function toDateInput(d) {
  const y = d.getFullYear()
  const m = ('0' + (d.getMonth() + 1)).slice(-2)
  const day = ('0' + d.getDate()).slice(-2)
  return `${y}-${m}-${day}`
}

// Shift a 'YYYY-MM-DD' (or ISO) date string by n days, returning 'YYYY-MM-DD'.
function addDays(dateStr, n) {
  const ymd = String(dateStr).slice(0, 10)
  const d = new Date(`${ymd}T00:00:00`)
  d.setDate(d.getDate() + n)
  return toDateInput(d)
}

export default {
  name: 'SharedAccountsStats',
  data() {
    return {
      loading: false,
      error: '',
      stats: null,
      preset: '30',
      fromDate: '',
      toDate: '',
      fromMenu: false,
      toMenu: false,
      sortBy: 'failures',
      sortDesc: true,
    }
  },
  computed: {
    arenaId() {
      return this.$store.getters.arena?.id
    },
    todayDate() {
      return toDateInput(new Date())
    },
    presetOptions() {
      return [
        { text: this.$t('sharedAccounts.stats.range.last7'), value: '7' },
        { text: this.$t('sharedAccounts.stats.range.last30'), value: '30' },
        { text: this.$t('sharedAccounts.stats.range.last90'), value: '90' },
        { text: this.$t('sharedAccounts.stats.range.custom'), value: 'custom' },
      ]
    },
    headers() {
      const t = (k) => this.$t(`sharedAccounts.stats.headers.${k}`)
      return [
        { text: t('game'), value: 'game', sortable: false },
        { text: t('accounts'), value: 'accounts', align: 'end' },
        { text: t('available'), value: 'available_now', align: 'end' },
        { text: t('borrows'), value: 'borrows', align: 'end' },
        { text: t('failures'), value: 'failures', align: 'end' },
        { text: t('failRate'), value: 'fail_rate', align: 'end' },
        { text: t('sessions'), value: 'distinct_sessions', align: 'end' },
      ]
    },
    byGame() {
      return Array.isArray(this.stats?.by_game) ? this.stats.by_game : []
    },
    summaryCards() {
      const s = this.stats?.summary
      if (!s) return []
      const overallFail =
        s.borrows + s.failures > 0 ? s.failures / (s.borrows + s.failures) : 0
      const l = (k) => this.$t(`sharedAccounts.stats.summary.${k}`)
      const tip = (k) => this.$t(`sharedAccounts.stats.tips.${k}`)
      return [
        {
          key: 'total',
          label: l('totalAccounts'),
          value: s.total_accounts,
          tip: tip('totalAccounts'),
        },
        {
          key: 'available',
          label: l('availableNow'),
          value: s.available_now,
          color: 'success',
          tip: tip('availableNow'),
        },
        {
          key: 'borrowed',
          label: l('borrowedNow'),
          value: s.borrowed_now,
          tip: tip('borrowedNow'),
        },
        {
          key: 'banned',
          label: l('banned'),
          value: s.banned,
          color: s.banned ? 'error' : null,
          tip: tip('banned'),
        },
        {
          key: 'borrows',
          label: l('borrows'),
          value: s.borrows,
          tip: tip('borrows'),
        },
        {
          key: 'failures',
          label: l('failures'),
          value: s.failures,
          color: s.failures ? 'warning' : null,
          tip: tip('failures'),
        },
        {
          key: 'failRate',
          label: l('failRate'),
          value: this.formatPercent(overallFail),
          color: this.failRateColor(overallFail),
          tip: tip('failRate'),
        },
        {
          key: 'sessions',
          label: l('distinctSessions'),
          value: s.distinct_sessions,
          tip: tip('distinctSessions'),
        },
      ]
    },
    // The echoed window start/end, tolerant of field-name variants.
    windowFrom() {
      const s = this.stats
      return (s && (s.data_from || s.from || s.date_gte)) || ''
    },
    windowTo() {
      const s = this.stats
      return (s && (s.data_to || s.to || s.date_lt)) || ''
    },
    resolvedWindow() {
      if (!this.windowFrom || !this.windowTo) return ''
      const from = this.formatDateOnly(this.windowFrom)
      // The end is exclusive (date_lt); show the inclusive last day.
      const to = this.formatDateOnly(addDays(this.windowTo, -1))
      return this.$t('sharedAccounts.stats.window', { from, to })
    },
    // Show a note when the window reaches before borrow/failure tracking began.
    showTrackingNote() {
      if (!this.windowFrom) return false
      return (
        new Date(String(this.windowFrom).slice(0, 10)) <
        new Date(STATS_TRACKING_START)
      )
    },
    trackingNote() {
      const date = new Date(STATS_TRACKING_START).toLocaleDateString(
        this.$i18n.locale,
      )
      return this.$t('sharedAccounts.stats.trackingNote', { date })
    },
  },
  watch: {
    arenaId() {
      this.fetchStats()
    },
  },
  created() {
    // Default custom-range fields to the last 30 days for when the user switches.
    const now = new Date()
    this.toDate = toDateInput(now)
    this.fromDate = toDateInput(new Date(now.getTime() - 30 * DAY_MS))
  },
  mounted() {
    if (this.arenaId) this.fetchStats()
  },
  methods: {
    ...mapActions(['showSnackbar']),
    onPresetChange() {
      // Preset ranges fetch immediately; custom waits for Apply.
      if (this.preset !== 'custom') this.fetchStats()
    },
    buildParams() {
      // Date-only, half-open [date_gte, date_lt) — both required.
      let gte
      let lt
      if (this.preset === 'custom') {
        if (!this.fromDate || !this.toDate) return ''
        gte = this.fromDate
        // The picked end date is inclusive; date_lt is exclusive → +1 day.
        lt = addDays(this.toDate, 1)
      } else {
        const today = toDateInput(new Date())
        lt = addDays(today, 1) // through the end of today
        gte = addDays(lt, -Number(this.preset)) // N days ending today
      }
      const params = new URLSearchParams()
      params.set('date_gte', gte)
      params.set('date_lt', lt)
      return params.toString()
    },
    async fetchStats() {
      if (!this.arenaId) return
      this.loading = true
      this.error = ''
      try {
        const response = await SharedAccountsApi.stats(
          this.arenaId,
          this.buildParams(),
        )
        this.stats = response.data
      } catch (e) {
        this.error = this.errorMessage(e)
        this.stats = null
      } finally {
        this.loading = false
      }
    },
    errorMessage(e) {
      const data = (e && e.response && e.response.data) || {}
      const key = data.l10n_message_key
      // Backend error keys aren't in our catalog; use a translation only if we
      // actually have it, else the backend message, else a generic fallback.
      if (key && (this.$te(key) || this.$te(key, 'en'))) return this.$t(key)
      return data.message || this.$t('sharedAccounts.stats.loadFailed')
    },
    formatDateOnly(value) {
      const ymd = String(value).slice(0, 10)
      const d = new Date(`${ymd}T00:00:00`)
      return isNaN(d.getTime()) ? ymd : d.toLocaleDateString(this.$i18n.locale)
    },
    formatPercent(fraction) {
      const pct = (Number(fraction) || 0) * 100
      return `${pct.toFixed(1)}%`
    },
    failRateColor(fraction) {
      const r = Number(fraction) || 0
      if (r >= 0.25) return 'error'
      if (r >= 0.1) return 'warning'
      return 'success'
    },
    goBack() {
      this.$router.push({
        name: 'navigationDrawer.configuration.sharedAccounts',
        params: { arena: this.$route.params.arena },
      })
    },
  },
}
</script>
